import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Info,
  RotateCcw,
} from "lucide-react";
import PageShell from "@/components/layout/PageShell";
import AssessmentLoopPrimer from "@/components/assessment/AssessmentLoopPrimer";
import RobustAssessmentResults from "@/components/assessment/RobustAssessmentResults";
import { saveProfileLocal } from "@/components/assessment/assessmentScoring";
import { PRE_ASSESSMENT_DISCLOSURE } from "@/components/assessment/evidenceMapping";
import {
  ACTIVITY_CATEGORY_OPTIONS,
  BODY_SIGNAL_OPTIONS,
  CHANGE_TIMING_OPTIONS,
  COGNITIVE_SIGNAL_OPTIONS,
  CONFLICT_CARRYOVER_OPTIONS,
  CORE_SCALE_SECTIONS,
  DIFFICULT_ENVIRONMENT_OPTIONS,
  EMOTION_OPTIONS,
  HELPFUL_ENVIRONMENT_OPTIONS,
  HELPFUL_FACTOR_OPTIONS,
  MOVEMENT_DAYS_OPTIONS,
  NOT_APPLICABLE_VALUE,
  REACTION_DIRECTION_OPTIONS,
  RECENT_CHANGE_OPTIONS,
  RECENT_PATTERN_OPTIONS,
  RECOVERY_DURATION_OPTIONS,
  ROBUST_ASSESSMENT_MODEL,
  ROBUST_ASSESSMENT_SCHEMA_VERSION,
  ROBUST_SECTION_LABELS,
  ROBUST_SECTION_ORDER,
  SOCIAL_SETTING_OPTIONS,
  UNKNOWN_VALUE,
  WORSENING_FACTOR_OPTIONS,
} from "@/components/assessment/robustAssessmentData";
import {
  buildRobustNervousSystemProfile,
  shouldAskCoachingResponse,
  shouldAskConflictCarryover,
  shouldAskSharedActivity,
} from "@/components/assessment/robustAssessmentEngine";

const initialAnswers = () => ({
  meta: {
    assessmentSchemaVersion: ROBUST_ASSESSMENT_SCHEMA_VERSION,
    assessmentModel: ROBUST_ASSESSMENT_MODEL,
  },
  recent: {
    recentPatterns: [],
    helpfulFactors: [],
    worseningFactors: [],
    context: "",
  },
  mobilization: {},
  control: {},
  expression: {
    emotions: [],
    bodySignals: [],
    cognitiveSignals: [],
    reactionDirections: [],
  },
  environment: {
    difficultEnvironments: [],
    helpfulEnvironments: [],
  },
  connection: {
    helpfulSettings: [],
  },
  capacity: {},
  resources: {},
  activity: {
    currentActivities: [],
    familiarActivities: [],
    preserveActivities: [],
  },
  schedule: {
    scheduledActivities: [],
    preferredActivities: [],
    familiarActivities: [],
  },
});

const ScaleQuestion = ({
  number,
  question,
  value,
  onChange,
  low,
  high,
  allowNotApplicable = false,
}) => (
  <div
    className="rounded-[20px] p-5 sm:p-6 forr-surface"
    style={{ border: "1px solid var(--forr-line-soft)" }}
  >
    <div className="flex items-start gap-4">
      <span className="mt-1 shrink-0 font-display text-sm forr-text-teal-deep">
        {String(number).padStart(2, "0")}
      </span>
      <div className="min-w-0 flex-1">
        <p className="font-display text-lg font-medium tracking-tight forr-text-ink">
          {question}
        </p>
        <div className="mt-4 grid grid-cols-5 gap-2 sm:gap-3">
          {[1, 2, 3, 4, 5].map((option) => (
            <button
              type="button"
              key={option}
              aria-pressed={value === option}
              onClick={() => onChange(option)}
              className="rounded-2xl py-3 text-sm font-medium transition-all sm:py-4"
              style={
                value === option
                  ? {
                      backgroundColor: "var(--forr-teal)",
                      border: "1px solid var(--forr-teal)",
                      color: "#fff",
                    }
                  : {
                      backgroundColor: "var(--forr-surface)",
                      border: "1px solid var(--forr-line-soft)",
                      color: "var(--forr-ink)",
                    }
              }
            >
              {option}
            </button>
          ))}
        </div>
        <div className="mt-3 flex justify-between gap-4 text-[10px] uppercase tracking-[0.16em] forr-text-mute sm:tracking-[0.22em]">
          <span className="max-w-[47%]">{low}</span>
          <span className="max-w-[47%] text-right">{high}</span>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            aria-pressed={value === UNKNOWN_VALUE}
            onClick={() => onChange(UNKNOWN_VALUE)}
            className="rounded-full px-3 py-2 text-xs forr-btn-secondary"
            style={
              value === UNKNOWN_VALUE
                ? { borderColor: "var(--forr-teal)", backgroundColor: "var(--forr-teal-tint)" }
                : undefined
            }
          >
            Not sure
          </button>
          {allowNotApplicable && (
            <button
              type="button"
              aria-pressed={value === NOT_APPLICABLE_VALUE}
              onClick={() => onChange(NOT_APPLICABLE_VALUE)}
              className="rounded-full px-3 py-2 text-xs forr-btn-secondary"
              style={
                value === NOT_APPLICABLE_VALUE
                  ? { borderColor: "var(--forr-teal)", backgroundColor: "var(--forr-teal-tint)" }
                  : undefined
              }
            >
              Does not apply
            </button>
          )}
        </div>
      </div>
    </div>
  </div>
);

const ChoiceGrid = ({ options, selected, onToggle, columns = "sm:grid-cols-2" }) => (
  <div className={`mt-4 grid gap-2 ${columns}`}>
    {options.map((raw) => {
      const option = typeof raw === "string" ? { key: raw, label: raw } : raw;
      const active = selected.includes(option.key);
      return (
        <button
          type="button"
          key={option.key}
          aria-pressed={active}
          onClick={() => onToggle(option.key)}
          className="rounded-2xl p-3.5 text-left text-sm leading-relaxed transition-all"
          style={
            active
              ? {
                  backgroundColor: "var(--forr-teal-soft)",
                  border: "1px solid var(--forr-teal)",
                  color: "var(--forr-ink)",
                }
              : {
                  backgroundColor: "var(--forr-surface)",
                  border: "1px solid var(--forr-line-soft)",
                  color: "var(--forr-ink-soft)",
                }
          }
        >
          <span className="flex items-start gap-2">
            <span
              className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md"
              style={{
                backgroundColor: active ? "var(--forr-teal)" : "transparent",
                border: active
                  ? "1px solid var(--forr-teal)"
                  : "1px solid var(--forr-line-strong)",
              }}
            >
              {active && <Check size={12} color="#fff" strokeWidth={2.5} />}
            </span>
            <span>{option.label}</span>
          </span>
        </button>
      );
    })}
  </div>
);

const SingleChoice = ({ options, value, onChange, columns = "sm:grid-cols-2" }) => (
  <div className={`mt-4 grid gap-2 ${columns}`}>
    {options.map((raw) => {
      const option = typeof raw === "string" ? { key: raw, label: raw } : raw;
      const active = value === option.key;
      return (
        <button
          type="button"
          key={option.key}
          aria-pressed={active}
          onClick={() => onChange(option.key)}
          className="rounded-2xl p-3.5 text-left text-sm leading-relaxed transition-all"
          style={
            active
              ? {
                  backgroundColor: "var(--forr-teal-soft)",
                  border: "1px solid var(--forr-teal)",
                  color: "var(--forr-ink)",
                }
              : {
                  backgroundColor: "var(--forr-surface)",
                  border: "1px solid var(--forr-line-soft)",
                  color: "var(--forr-ink-soft)",
                }
          }
        >
          {option.label}
        </button>
      );
    })}
  </div>
);

const QuestionCard = ({ number, question, help, children }) => (
  <div
    className="rounded-[20px] p-5 sm:p-6 forr-surface"
    style={{ border: "1px solid var(--forr-line-soft)" }}
  >
    <div className="flex items-start gap-4">
      <span className="mt-1 shrink-0 font-display text-sm forr-text-teal-deep">
        {String(number).padStart(2, "0")}
      </span>
      <div className="min-w-0 flex-1">
        <p className="font-display text-lg font-medium tracking-tight forr-text-ink">
          {question}
        </p>
        {help && <p className="mt-2 text-xs leading-relaxed forr-text-mute">{help}</p>}
        {children}
      </div>
    </div>
  </div>
);

const ProgressRail = ({ step }) => {
  const current = ROBUST_SECTION_ORDER.indexOf(step);
  return (
    <div className="mb-9 overflow-x-auto pb-2">
      <div className="flex min-w-max items-center gap-2">
        {ROBUST_SECTION_ORDER.map((section, index) => (
          <React.Fragment key={section}>
            <div
              className="flex h-7 w-7 items-center justify-center rounded-full text-[11px] font-medium"
              style={
                section === step
                  ? { backgroundColor: "var(--forr-teal)", color: "#fff" }
                  : index < current
                    ? {
                        backgroundColor: "var(--forr-teal-soft)",
                        border: "1px solid var(--forr-teal)",
                        color: "var(--forr-teal-deep)",
                      }
                    : {
                        backgroundColor: "var(--forr-surface)",
                        border: "1px solid var(--forr-line-soft)",
                        color: "var(--forr-ink-mute)",
                      }
              }
            >
              {index < current ? <Check size={12} /> : index + 1}
            </div>
            {index < ROBUST_SECTION_ORDER.length - 1 && (
              <span className="h-px w-4 sm:w-8" style={{ backgroundColor: "var(--forr-line-strong)" }} />
            )}
          </React.Fragment>
        ))}
      </div>
      <div className="mt-3 text-[10px] uppercase tracking-[0.2em] forr-text-mute">
        {ROBUST_SECTION_LABELS[step]}
      </div>
    </div>
  );
};

const RobustAssessment = () => {
  const [step, setStep] = useState("welcome");
  const [answers, setAnswers] = useState(initialAnswers);
  const [disclosureAcknowledged, setDisclosureAcknowledged] = useState(false);
  const startRef = useRef(null);
  const savedResultRef = useRef(false);

  useEffect(() => {
    if (step !== "welcome") {
      startRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [step]);

  useEffect(() => {
    if (step === "results" && !savedResultRef.current) {
      savedResultRef.current = true;
      saveProfileLocal(answers);
    }
  }, [answers, step]);

  const profile = useMemo(
    () => buildRobustNervousSystemProfile(answers),
    [answers],
  );

  const setValue = (section, key, value) =>
    setAnswers((current) => ({
      ...current,
      [section]: { ...current[section], [key]: value },
    }));

  const toggle = (section, key, value, exclusive = []) =>
    setAnswers((current) => {
      const selected = Array.isArray(current[section]?.[key])
        ? current[section][key]
        : [];
      let next;
      if (selected.includes(value)) {
        next = selected.filter((item) => item !== value);
      } else if (exclusive.includes(value)) {
        next = [value];
      } else {
        next = [...selected.filter((item) => !exclusive.includes(item)), value];
      }
      return {
        ...current,
        [section]: { ...current[section], [key]: next },
      };
    });

  const scaleSectionComplete = (sectionId) => {
    const section = CORE_SCALE_SECTIONS.find((item) => item.id === sectionId);
    return section.questions.every(
      (question) => answers[sectionId]?.[question.key] !== undefined,
    );
  };

  const canProceed = () => {
    if (step === "welcome") return disclosureAcknowledged;
    if (step === "recent") {
      const recent = answers.recent;
      if (!recent.recentChange) return false;
      if (!["none", UNKNOWN_VALUE].includes(recent.recentChange)) {
        if (!recent.recentPatterns.length || !recent.changeTiming) return false;
      }
      return recent.helpfulFactors.length > 0 && recent.worseningFactors.length > 0;
    }
    if (["mobilization", "capacity", "resources"].includes(step)) {
      return scaleSectionComplete(step);
    }
    if (step === "control") {
      return (
        scaleSectionComplete(step) &&
        answers.control.recoveryDuration !== undefined
      );
    }
    if (step === "expression") {
      return ["emotions", "bodySignals", "cognitiveSignals", "reactionDirections"].every(
        (key) => answers.expression[key].length > 0,
      );
    }
    if (step === "environment") {
      const noClearDifficultEnvironment = answers.environment.difficultEnvironments.some(
        (item) => ["none of these", "not sure"].includes(item),
      );
      return (
        answers.environment.difficultEnvironments.length > 0 &&
        (noClearDifficultEnvironment ||
(answers.environment.effectMagnitude !== undefined &&
  answers.environment.environmentalControl !== undefined)) &&
        answers.environment.helpfulEnvironments.length > 0
      );
    }
    if (step === "connection") {
      if (!answers.connection.helpfulSettings.length) return false;
      if (answers.connection.supportAvailability === undefined) return false;
      if (answers.connection.socialEvaluation === undefined) return false;
      if (
        shouldAskCoachingResponse(answers) &&
        answers.connection.coachingResponse === undefined
      )
        return false;
      if (
        shouldAskConflictCarryover(answers) &&
        answers.connection.conflictCarryover === undefined
      )
        return false;
      if (
        shouldAskSharedActivity(answers) &&
        answers.connection.sharedActivityBenefit === undefined
      )
        return false;
      return true;
    }
    if (step === "activity") {
      const noRegularActivity = answers.activity.currentActivities.includes(
        "no regular activity right now",
      );
      return (
        answers.activity.currentActivities.length > 0 &&
        answers.activity.movementDays !== undefined &&
        (noRegularActivity ||
(answers.activity.familiarActivities.length > 0 &&
  answers.activity.preserveActivities.length > 0))
      );
    }
    return true;
  };

  const goNext = () => {
    if (!canProceed()) return;
    if (step === "welcome") return setStep("recent");
    const index = ROBUST_SECTION_ORDER.indexOf(step);
    if (index === ROBUST_SECTION_ORDER.length - 1) setStep("results");
    else setStep(ROBUST_SECTION_ORDER[index + 1]);
  };

  const goBack = () => {
    const index = ROBUST_SECTION_ORDER.indexOf(step);
    if (index <= 0) setStep("welcome");
    else setStep(ROBUST_SECTION_ORDER[index - 1]);
  };

  const restart = () => {
    savedResultRef.current = false;
    setAnswers(initialAnswers());
    setStep("welcome");
  };

  if (step === "results") {
    return (
      <RobustAssessmentResults
        answers={answers}
        profile={profile}
        onRetake={restart}
      />
    );
  }

  const scaleSection = CORE_SCALE_SECTIONS.find((item) => item.id === step);

  return (
    <PageShell>
      <section className="relative overflow-hidden pb-24 pt-24 forr-canvas sm:pt-32">
        <div className="pointer-events-none absolute -top-48 left-1/2 h-[900px] w-[900px] -translate-x-1/2 opacity-55 forr-radial-teal" />
        <div className="relative mx-auto max-w-[1050px] px-5 sm:px-10">
          <nav
            aria-label="Breadcrumb"
            className="mb-9 flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] forr-text-mute"
          >
            <Link to="/">Home</Link>
            <span>›</span>
            <span>Adaptive Assessment™</span>
          </nav>
          <div ref={startRef} className="scroll-mt-24" />

          {step !== "welcome" && <ProgressRail step={step} />}

          <AnimatePresence mode="wait">
            {step === "welcome" && (
              <motion.div
                key="welcome"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
              >
                <div className="forr-overline">Adaptive Assessment™ · Version 5</div>
                <h1 className="mt-5 max-w-4xl font-display text-[40px] font-medium leading-[1.03] tracking-[-0.035em] forr-text-ink sm:text-6xl">
                  Understand how your nervous system mobilizes, maintains control,
                  and recovers.
                </h1>
                <p className="mt-7 max-w-3xl text-lg font-light leading-relaxed forr-text-soft">
                  This baseline separates nervous-system reaction strength, usable
                  energy, behavioral control, attention, recovery, environment,
                  connection, physical capacity, and recovery resources. Current
                  nervous-system state matching remains inside APOS Companion.
                </p>

                <div className="mt-9 grid gap-3 sm:grid-cols-3">
                  {[
                    ["01", "Nervous-system response", "Detection, mobilization, energy, pressure, and control"],
                    ["02", "Context and capacity", "Environment, connection, physical ability, and recovery resources"],
                    ["03", "Personalized results", "A descriptive nervous-system profile rather than one universal score"],
                  ].map(([number, title, body]) => (
                    <div
                      key={number}
                      className="rounded-2xl p-5 forr-surface"
                      style={{ border: "1px solid var(--forr-line-soft)" }}
                    >
                      <div className="forr-overline">{number}</div>
                      <div className="mt-2 font-display text-xl forr-text-ink">{title}</div>
                      <p className="mt-2 text-xs leading-relaxed forr-text-soft">{body}</p>
                    </div>
                  ))}
                </div>

                <AssessmentLoopPrimer />

                <div
                  className="mt-10 max-w-3xl rounded-2xl p-6 forr-canvas-warm"
                  style={{ border: "1px solid var(--forr-line-soft)" }}
                >
                  <div className="flex items-start gap-3">
                    <Info size={17} className="mt-0.5 shrink-0 forr-text-teal-deep" />
                    <p className="text-sm leading-relaxed forr-text-soft">
                      {PRE_ASSESSMENT_DISCLOSURE}
                    </p>
                  </div>
                  <label className="mt-5 flex cursor-pointer items-start gap-3">
                    <input
                      type="checkbox"
                      checked={disclosureAcknowledged}
                      onChange={(event) =>
                        setDisclosureAcknowledged(event.target.checked)
                      }
                      className="mt-1"
                    />
                    <span className="text-sm forr-text-ink">
                      I have read this and want to continue.
                    </span>
                  </label>
                </div>

                <button
                  type="button"
                  disabled={!disclosureAcknowledged}
                  onClick={goNext}
                  className="mt-8 inline-flex items-center gap-3 rounded-full px-7 py-4 text-sm font-semibold forr-btn-primary disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Begin the assessment <ArrowRight size={16} />
                </button>
              </motion.div>
            )}

            {step === "recent" && (
              <motion.div key="recent" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}>
                <div className="forr-overline">01 · Recent change and context</div>
                <h2 className="mt-4 max-w-3xl font-display text-4xl font-medium tracking-[-0.025em] forr-text-ink sm:text-5xl">
                  What has changed recently?
                </h2>
                <p className="mt-5 max-w-2xl leading-relaxed forr-text-soft">
                  Think about the past four weeks compared with how you usually function.
                </p>
                <div className="mt-10 space-y-5">
                  <QuestionCard number={1} question="Over the past four weeks, have you noticed a meaningful change in how your nervous system has been responding?">
                    <SingleChoice options={RECENT_CHANGE_OPTIONS} value={answers.recent.recentChange} onChange={(value) => setValue("recent", "recentChange", value)} />
                  </QuestionCard>

                  {![undefined, "none", UNKNOWN_VALUE].includes(answers.recent.recentChange) && (
                    <>
                      <QuestionCard number={2} question="Which changes have you noticed?">
                        <ChoiceGrid options={RECENT_PATTERN_OPTIONS} selected={answers.recent.recentPatterns} onToggle={(value) => toggle("recent", "recentPatterns", value, ["not sure"])} />
                      </QuestionCard>
                      <QuestionCard number={3} question="When did you first notice this change?">
                        <SingleChoice options={CHANGE_TIMING_OPTIONS} value={answers.recent.changeTiming} onChange={(value) => setValue("recent", "changeTiming", value)} />
                      </QuestionCard>
                    </>
                  )}

                  <QuestionCard number={4} question="Which things have directly helped?">
                    <ChoiceGrid options={HELPFUL_FACTOR_OPTIONS} selected={answers.recent.helpfulFactors} onToggle={(value) => toggle("recent", "helpfulFactors", value, ["nothing clearly helped", "not sure"])} />
                  </QuestionCard>
                  <QuestionCard number={5} question="Which things have specifically made it worse?">
                    <ChoiceGrid options={WORSENING_FACTOR_OPTIONS} selected={answers.recent.worseningFactors} onToggle={(value) => toggle("recent", "worseningFactors", value, ["nothing clearly made it worse", "not sure"])} />
                  </QuestionCard>
                  <QuestionCard number={6} question="Is there anything important about when this started or changed?" help="Optional. A short sentence is enough.">
                    <textarea
                      value={answers.recent.context}
                      onChange={(event) => setValue("recent", "context", event.target.value)}
                      rows={4}
                      className="mt-4 w-full rounded-2xl p-4 text-sm forr-surface forr-text-ink"
                      style={{ border: "1px solid var(--forr-line-soft)" }}
                      placeholder="Workload, sleep, relationship changes, injury, travel, training, or another relevant change..."
                    />
                  </QuestionCard>
                </div>
              </motion.div>
            )}

            {scaleSection && (
              <motion.div key={step} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}>
                <div className="forr-overline">{scaleSection.label}</div>
                <h2 className="mt-4 max-w-3xl font-display text-4xl font-medium tracking-[-0.025em] forr-text-ink sm:text-5xl">
                  {scaleSection.heading}
                </h2>
                <p className="mt-5 max-w-3xl leading-relaxed forr-text-soft">{scaleSection.body}</p>
                <div className="mt-10 space-y-5">
                  {scaleSection.questions.map((question, index) => (
                    <ScaleQuestion
                      key={question.key}
                      number={index + 1}
                      question={question.q}
                      value={answers[step][question.key]}
                      onChange={(value) => setValue(step, question.key, value)}
                      low={question.low}
                      high={question.high}
                    />
                  ))}
                  {step === "control" && (
                    <QuestionCard number={scaleSection.questions.length + 1} question="How long does it usually take before you can think clearly and function normally again?">
                      <SingleChoice options={RECOVERY_DURATION_OPTIONS} value={answers.control.recoveryDuration} onChange={(value) => setValue("control", "recoveryDuration", value)} />
                    </QuestionCard>
                  )}
                </div>
              </motion.div>
            )}

            {step === "expression" && (
              <motion.div key="expression" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}>
                <div className="forr-overline">04 · How the nervous-system state appears</div>
                <h2 className="mt-4 max-w-3xl font-display text-4xl font-medium tracking-[-0.025em] forr-text-ink sm:text-5xl">
                  How does a strong nervous-system state show up for you?
                </h2>
                <p className="mt-5 max-w-3xl leading-relaxed forr-text-soft">
                  Emotion is meaningful nervous-system information. APOS combines it with physical signals, attention, reaction direction, energy, and behavioral control.
                </p>
                <div className="mt-10 space-y-5">
                  <QuestionCard number={1} question="When your nervous-system state shifts strongly, which feelings usually show up?">
                    <ChoiceGrid options={EMOTION_OPTIONS} selected={answers.expression.emotions} onToggle={(value) => toggle("expression", "emotions", value, ["no consistent feeling"])} />
                  </QuestionCard>
                  <QuestionCard number={2} question="Which physical changes usually show up with that nervous-system state?">
                    <ChoiceGrid options={BODY_SIGNAL_OPTIONS} selected={answers.expression.bodySignals} onToggle={(value) => toggle("expression", "bodySignals", value, ["no clear physical change"])} />
                  </QuestionCard>
                  <QuestionCard number={3} question="Which changes in thinking or attention usually show up?">
                    <ChoiceGrid options={COGNITIVE_SIGNAL_OPTIONS} selected={answers.expression.cognitiveSignals} onToggle={(value) => toggle("expression", "cognitiveSignals", value, ["no clear cognitive change"])} />
                  </QuestionCard>
                  <QuestionCard number={4} question="When your nervous-system state shifts strongly, what direction does your reaction usually take?">
                    <ChoiceGrid options={REACTION_DIRECTION_OPTIONS} selected={answers.expression.reactionDirections} onToggle={(value) => toggle("expression", "reactionDirections", value, ["mixed or uncertain"])} />
                  </QuestionCard>
                </div>
              </motion.div>
            )}

            {step === "environment" && (
              <motion.div key="environment" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}>
                <div className="forr-overline">05 · Environment</div>
                <h2 className="mt-4 max-w-3xl font-display text-4xl font-medium tracking-[-0.025em] forr-text-ink sm:text-5xl">
                  Which environments change how you feel or function?
                </h2>
                <p className="mt-5 max-w-3xl leading-relaxed forr-text-soft">
                  Environment changes total nervous-system demand. It can also change which intervention is easiest to begin, control, and complete.
                </p>
                <div className="mt-10 space-y-5">
                  <QuestionCard number={1} question="Which environments tend to make it harder for you to feel focused, steady, or in control?">
                    <ChoiceGrid options={DIFFICULT_ENVIRONMENT_OPTIONS} selected={answers.environment.difficultEnvironments} onToggle={(value) => toggle("environment", "difficultEnvironments", value, ["none of these", "not sure"])} />
                  </QuestionCard>
                  <ScaleQuestion number={2} question="How strongly do those environments change how you feel or function?" value={answers.environment.effectMagnitude} onChange={(value) => setValue("environment", "effectMagnitude", value)} low="1 · Very little" high="5 · A great deal" allowNotApplicable />
                  <ScaleQuestion number={3} question="When an environment is making things harder, how easily can you reduce the stimulation, change the setting, or leave?" value={answers.environment.environmentalControl} onChange={(value) => setValue("environment", "environmentalControl", value)} low="1 · Rarely possible" high="5 · Usually possible" allowNotApplicable />
                  <QuestionCard number={4} question="Which environments usually help you feel or function better?">
                    <ChoiceGrid options={HELPFUL_ENVIRONMENT_OPTIONS} selected={answers.environment.helpfulEnvironments} onToggle={(value) => toggle("environment", "helpfulEnvironments", value, ["no consistent setting", "not sure"])} />
                  </QuestionCard>
                </div>
              </motion.div>
            )}

            {step === "connection" && (
              <motion.div key="connection" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}>
                <div className="forr-overline">06 · Connection and social context</div>
                <h2 className="mt-4 max-w-3xl font-display text-4xl font-medium tracking-[-0.025em] forr-text-ink sm:text-5xl">
                  How do people and social settings change your nervous-system reaction?
                </h2>
                <p className="mt-5 max-w-3xl leading-relaxed forr-text-soft">
                  Social format changes attention, control, follow-through, evaluation pressure, and recovery. APOS uses one clear professional format: 1:1 coaching.
                </p>
                <div className="mt-10 space-y-5">
                  <QuestionCard number={1} question="Which social settings usually help you function best?">
                    <ChoiceGrid options={SOCIAL_SETTING_OPTIONS} selected={answers.connection.helpfulSettings} onToggle={(value) => toggle("connection", "helpfulSettings", value, ["it depends on the situation", "no consistent preference", "not sure"])} />
                  </QuestionCard>
                  {shouldAskCoachingResponse(answers) && (
                    <ScaleQuestion number={2} question="How much does 1:1 coaching improve your focus, control, or follow-through?" value={answers.connection.coachingResponse} onChange={(value) => setValue("connection", "coachingResponse", value)} low="1 · Little effect" high="5 · Strong effect" allowNotApplicable />
                  )}
                  <ScaleQuestion number={3} question="When you need support, how reliably is it available?" value={answers.connection.supportAvailability} onChange={(value) => setValue("connection", "supportAvailability", value)} low="1 · Rarely" high="5 · Reliably" />
                  <ScaleQuestion number={4} question="How much does being watched, compared, or evaluated change your body’s reaction?" value={answers.connection.socialEvaluation} onChange={(value) => setValue("connection", "socialEvaluation", value)} low="1 · Very little" high="5 · A great deal" allowNotApplicable />
                  {shouldAskConflictCarryover(answers) && (
                    <QuestionCard number={5} question="After interpersonal conflict, how long does the reaction tend to stay active?" help="This adds context-specific recovery information; it does not independently determine exercise intensity.">
                      <SingleChoice options={CONFLICT_CARRYOVER_OPTIONS} value={answers.connection.conflictCarryover} onChange={(value) => setValue("connection", "conflictCarryover", value)} />
                    </QuestionCard>
                  )}
                  {shouldAskSharedActivity(answers) && (
                    <ScaleQuestion number={6} question="When support is needed, does moving or doing an activity with someone usually help more than talking alone?" value={answers.connection.sharedActivityBenefit} onChange={(value) => setValue("connection", "sharedActivityBenefit", value)} low="1 · Usually not" high="5 · Strongly" allowNotApplicable />
                  )}
                </div>
              </motion.div>
            )}

            {step === "activity" && (
              <motion.div key="activity" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}>
                <div className="forr-overline">09 · Activity and recreation</div>
                <h2 className="mt-4 max-w-3xl font-display text-4xl font-medium tracking-[-0.025em] forr-text-ink sm:text-5xl">
                  What movement, sport, or program is already part of your life?
                </h2>
                <p className="mt-5 max-w-3xl leading-relaxed forr-text-soft">
                  Keep this baseline brief. After the assessment, the APOS Calendar will let you add full sports, programs, exercises, recurring sessions, coaching, rehabilitation, and other scheduled demands for the engine to read.
                </p>
                <div className="mt-10 space-y-5">
                  <QuestionCard number={1} question="Which activities or programs are currently part of your life?">
                    <ChoiceGrid options={ACTIVITY_CATEGORY_OPTIONS} selected={answers.activity.currentActivities} onToggle={(value) => toggle("activity", "currentActivities", value, ["no regular activity right now"])} />
                  </QuestionCard>
                  <QuestionCard number={2} question="How many days per week do you usually perform purposeful movement, exercise, or sport?">
                    <SingleChoice options={MOVEMENT_DAYS_OPTIONS} value={answers.activity.movementDays} onChange={(value) => setValue("activity", "movementDays", value)} />
                  </QuestionCard>
                  {!answers.activity.currentActivities.includes("no regular activity right now") && (
          <>
            <QuestionCard number={3} question="Which activities are familiar enough that you can perform them without having to learn while under pressure?">
              <ChoiceGrid options={ACTIVITY_CATEGORY_OPTIONS} selected={answers.activity.familiarActivities} onToggle={(value) => toggle("activity", "familiarActivities", value, ["no regular activity right now"])} />
            </QuestionCard>
            <QuestionCard number={4} question="Which activities do you want APOS to preserve rather than replace?">
              <ChoiceGrid options={ACTIVITY_CATEGORY_OPTIONS} selected={answers.activity.preserveActivities} onToggle={(value) => toggle("activity", "preserveActivities", value, ["no regular activity right now"])} />
            </QuestionCard>
          </>
        )}
                  <div className="rounded-2xl p-5 forr-panel-information">
                    <div className="forr-overline">After this assessment</div>
                    <p className="mt-2 text-sm leading-relaxed forr-text-soft">
                      Open Plan in APOS Companion to add full recurring programs, activities, start times, duration, relative effort, social format, competition, location, modification options, and normal recovery cost.
                    </p>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {step !== "welcome" && (
            <div className="mt-10 flex flex-wrap items-center justify-between gap-4 border-t pt-6" style={{ borderColor: "var(--forr-line-soft)" }}>
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={goBack} className="inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm forr-btn-secondary">
                  <ArrowLeft size={15} /> Back
                </button>
                <button type="button" onClick={restart} className="inline-flex items-center gap-2 rounded-full px-4 py-3 text-xs forr-btn-secondary">
                  <RotateCcw size={14} /> Start over
                </button>
              </div>
              <button
                type="button"
                onClick={goNext}
                disabled={!canProceed()}
                className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium forr-btn-primary disabled:cursor-not-allowed disabled:opacity-35"
              >
                {step === "activity" ? "Create my nervous-system profile" : "Continue"}
                <ArrowRight size={15} />
              </button>
            </div>
          )}
        </div>
      </section>
    </PageShell>
  );
};

export default RobustAssessment;
